import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Squares2X2Icon,
  CheckCircleIcon,
  BuildingOffice2Icon,
  Cog6ToothIcon,
  ChevronDoubleLeftIcon,
  ChevronRightIcon,
  SunIcon,
  MoonIcon,
  ChartBarIcon,
  LightBulbIcon,
  FolderIcon,
  XMarkIcon,
  Bars3Icon
} from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import { SlBell } from 'react-icons/sl';
import { FiUser, FiLogOut } from 'react-icons/fi';
import { FaRegBuilding } from "react-icons/fa";
import DropDown from './components/Dropdown';
import Loader from './components/Loader';
import { Outlet } from 'react-router-dom';
import { usePageTitle } from '../contexts/PageTitleContext';



const menuItems = [
  {
    key: 'dashboard',
    title: 'Dashboard',
    icon: ChartBarIcon,
    submenu: [
      { name: 'Dashboard', path: '/dashboard' },
    ],
  },
  {
    key: 'sequencias',
    title: 'Sequência',
    icon: Squares2X2Icon,
    submenu: [
      { name: 'Minhas Sequências', path: '/sequencia' },
      { name: 'Ordem de Serviço',  },
      { name: 'Templates de Sequências', path: '/templates' },
    ],
  },
  {
    key: 'gestao',
    title: 'Gestão',
    icon: CheckCircleIcon,
    submenu: [
      { name: 'To Do', path: '/todo_do' },
      { name: 'Recorrentes', path: '/detalhes_tarefas' },
    ],
  },
  {
    key: 'base_dados',
    title: 'Base de Dados',
    icon: BuildingOffice2Icon,
    submenu: [
      { name: 'Clientes', path: '/clientes' },
      { name: 'Colaboradores', path: '/colaboradores' },
    ],
  },
  {
    key: 'inteligencia',
    title: 'Inteligência',
    icon: LightBulbIcon,
    submenu: [
      { name: 'Automações', path: '/lista-automacoes' },
      { name: 'IA', },
    ],
  },
  {
    key: 'arquivo',
    title: 'Arquivo',
    icon: FolderIcon,
    submenu: [
      { name: 'Leva arquivo', path: '/leva-arquivos' },
      { name: 'Pastas', path: '/folders' },
    ],
  },
  {
    key: 'Configurações',
    title: 'Configurações',
    icon: Cog6ToothIcon,
    submenu: [
      { name: 'Cadastros', path: '/configurações' },
      { name: 'Listas de Ordens de Serviços', path: '/lista-ordens' },
      { name: 'Emails', path: '/emails' },
    ],
  },
];

const BaseLayout = () => {
  const { title } = usePageTitle();
  const [isLoading, setIsLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(true);
  const [expandedItem, setExpandedItem] = useState(null);
  const [notificacoesAberto, setNotificacoesAberto] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const notificacoesRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 100);

    return () => clearTimeout(timer);
  }, []);


  const user = {
    name: 'Marcelly Pereira',
    email: 'marcelly@email.com',
    avatar: 'https://images.pexels.com/photos/6436445/pexels-photo-6436445.jpeg',
  };


  const dropdownOptions = [
    { label: 'Minha conta', icon: FiUser, onClick: () => navigate('/perfil') },
    { label: 'Painel Administrativo', icon: FaRegBuilding, onClick: () => navigate('/painel-administrativo') },
    { label: 'Sair', icon: FiLogOut, onClick: () => console.log('Sair') },
  ];
  
  const getActiveItemFromPath = (path) => {
    for (const item of menuItems) {
      if (item.submenu) {
        for (const sub of item.submenu) {
          if (path === sub.path) {
            return item.key;
          }
        }
      }
    }
    return 'sequencia';
  };
  
  const [activeItem, setActiveItem] = useState(() => getActiveItemFromPath(location.pathname));
  
  useEffect(() => {
    setActiveItem(getActiveItemFromPath(location.pathname));
  }, [location.pathname]);
  
  useEffect(() => {
    if (isMobile && isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isMobile, isOpen]);
  
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth < 768) {
        setIsOpen(false);
      } else {
        setIsOpen(true);
      }
    };
    
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);
  
  const handleItemClick = (key) => {
    if (!isOpen) {
      setIsOpen(true);
      setTimeout(() => {
        setExpandedItem(expandedItem === key ? null : key);
      }, 150);
    } else {
      setExpandedItem(expandedItem === key ? null : key);
    }
    setActiveItem(key);
    
    if (isMobile) {
      setTimeout(() => {
        setIsOpen(false);
      }, 300);
    }
  };

  const alternarNotificacoes = () => {
    setNotificacoesAberto(!notificacoesAberto);
  };

  const alternarTema = () => {
    setIsDarkMode(!isDarkMode);
  };

  useEffect(() => {
    function handleClickFora(event) {
      if (
        notificacoesRef.current &&
        !notificacoesRef.current.contains(event.target)
      ) {
        setNotificacoesAberto(false);
      }
    }

    document.addEventListener('mousedown', handleClickFora);

    return () => {
      document.removeEventListener('mousedown', handleClickFora);
    };
  }, []);

  const sidebarVariants = {
    open: {
      x: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30
      }
    },
    closed: {
      x: "-100%",
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30
      }
    }
  };

  if (isLoading) {
    return <Loader fullScreen />;
  }

  return (
    <div className="flex h-screen relative">
      <AnimatePresence>
        {isMobile && isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-slate-900/20 backdrop-blur z-40 cursor-pointer"
            onClick={() => setIsOpen(false)}
          />
        )}
      </AnimatePresence>
      
      {!isMobile && (
        <motion.aside
          layout
          className="bg-white border-r border-slate-100 flex flex-col z-50 fixed left-0 top-0 h-full shadow-sm"
          style={{ width: isOpen ? '240px' : '70px' }}
        >
          <div className="flex items-center h-14 px-4 border-b border-slate-100 flex-shrink-0 bg-white">
            <motion.div layout className={`flex items-center ${isOpen ? '' : 'justify-center w-full'}`}>
              <motion.div layout className="flex-shrink-0">
                <button onClick={() => navigate('/home')}>
                  <img
                    src={isOpen ? "/static/assets/images/logo.png" : "/static/assets/images/logo-sm.png"}
                    alt="Logo"
                    className="h-7 w-auto"
                  />
                </button>
              </motion.div>
            </motion.div>
          </div>

          <div className="flex-1 py-4 text-sm">
            <div className="space-y-1 px-3">
              {menuItems.map((item) => {
                const Icon = item.icon;
                const isExpanded = expandedItem === item.key;
                const isActive = activeItem === item.key;

                return (
                  <div key={item.key}>
                    <motion.button
                      layout
                      onClick={() => {
                        handleItemClick(item.key);
                        if (!isOpen || !item.submenu || item.submenu.length === 0) {
                          if (item.submenu && item.submenu.length > 0) {
                            navigate(item.submenu[0].path);
                          }
                        }
                      }}
                      className={`relative flex h-10 w-full items-center rounded-lg transition-colors ${
                        isActive
                          ? 'bg-slate-50 text-[#3c0f55]'
                          : 'text-slate-600 hover:bg-slate-50 hover:text-[#3c0f55]'
                      }`}
                    >
                      {isActive && (
                        <motion.div
                          className="absolute left-0 top-0 bottom-0 w-1 bg-[#3c0f55] rounded-r-full"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                        />
                      )}
                      <motion.div
                        layout
                        className="grid h-full w-10 place-content-center text-lg flex-shrink-0"
                      >
                        <Icon className="h-5 w-5 text-gray-800" />
                      </motion.div>

                      {isOpen && (
                        <motion.span
                          layout
                          initial={{ opacity: 0, y: 12 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.125 }}
                          className="text-sm font-medium ml-3 flex-1 text-left text-gray-800"
                        >
                          {item.title}
                        </motion.span>
                      )}

                      {isOpen && item.submenu && (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.125 }}
                          className="mr-3"
                        >
                          <motion.div
                            animate={{ rotate: isExpanded || isActive ? 90 : 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            <ChevronRightIcon className="w-3.5 h-3.5 text-gray-800" />
                          </motion.div>
                        </motion.div>
                      )}
                    </motion.button>

                    {isOpen && item.submenu && (
                      <AnimatePresence initial={false}>
                        {isExpanded && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.15 }}
                            className="ml-10 mt-1 space-y-1"
                          >
                            {item.submenu.map((sub, index) => (
                              <Link 
                                to={sub.path}
                                key={index}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActiveItem(item.key);
                                }}
                              >
                                <motion.div
                                  initial={{ opacity: 0, x: -10 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ delay: index * 0.05 }}
                                  className={`flex items-center px-3 py-1.5 rounded-md cursor-pointer text-slate-600 hover:bg-slate-50 hover:text-[#3c0f55] text-xs ${
                                    location.pathname === sub.path ? 'bg-slate-50 text-[#3c0f55]' : ''
                                  }`}
                                >
                                  {sub.name}
                                </motion.div>
                              </Link>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <motion.button
            layout
            onClick={() => setIsOpen(!isOpen)}
            className="border-t border-slate-100 transition-colors hover:bg-slate-50 flex-shrink-0"
          >
            <div className="flex items-center p-3">
              <motion.div layout className="grid size-10 place-content-center text-lg flex-shrink-0">
                <ChevronDoubleLeftIcon
                  className={`w-4 h-4 text-slate-500 transition-transform ${!isOpen && 'rotate-180'}`}
                />
              </motion.div>
              {isOpen && (
                <motion.span
                  layout
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.125 }}
                  className="text-sm font-medium text-slate-600 ml-3"
                >
                  Ocultar
                </motion.span>
              )}
            </div>
          </motion.button>
        </motion.aside>
      )}
      
      {isMobile && (
        <AnimatePresence>
          <motion.aside
            className="fixed inset-y-0 left-0 z-50 w-[240px] bg-white shadow-xl flex flex-col"
            variants={sidebarVariants}
            initial="closed"
            animate={isOpen ? "open" : "closed"}
          >
            <div className="flex items-center justify-between h-14 px-4 border-b border-slate-100">
              <div className="flex items-center">
                <img 
                  src="/static/assets/images/logo.png" 
                  alt="Logo" 
                  className="h-7 w-auto" 
                />
              </div>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setIsOpen(false)}
              >
                <XMarkIcon className="h-5 w-5 text-gray-500" />
              </motion.button>
            </div>
            
            <div className="flex-1 py-4 text-sm overflow-y-auto">
              <div className="space-y-1 px-3">
                {menuItems.map((item) => {
                  const Icon = item.icon;
                  const isExpanded = expandedItem === item.key;
                  const isActive = activeItem === item.key;

                  return (
                    <div key={item.key}>
                      <motion.button
                        onClick={() => handleItemClick(item.key)}
                        className={`relative flex h-10 w-full items-center rounded-lg transition-colors ${
                          isActive
                            ? 'bg-slate-50 text-[#3c0f55]'
                            : 'text-slate-600 hover:bg-slate-50 hover:text-[#3c0f55]'
                        }`}
                      >
                        {isActive && (
                          <motion.div
                            className="absolute left-0 top-0 bottom-0 w-1 bg-[#3c0f55] rounded-r-full"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                          />
                        )}
                        <div className="grid h-full w-10 place-content-center text-lg flex-shrink-0">
                          <Icon className="h-5 w-5 text-gray-800" />
                        </div>
                        <span className="text-sm font-medium ml-3 flex-1 text-left text-gray-800">
                          {item.title}
                        </span>
                        {item.submenu && (
                          <div className="mr-3">
                            <motion.div
                              animate={{ rotate: isExpanded ? 90 : 0 }}
                              transition={{ duration: 0.2 }}
                            >
                              <ChevronRightIcon className="w-3.5 h-3.5 text-gray-800" />
                            </motion.div>
                          </div>
                        )}
                      </motion.button>

                      {item.submenu && (
                        <AnimatePresence initial={false}>
                          {isExpanded && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.15 }}
                              className="ml-10 mt-1 space-y-1"
                            >
                              {item.submenu.map((sub, index) => (
                                <Link 
                                  to={sub.path}
                                  key={index}
                                  onClick={() => {
                                    setActiveItem(item.key);
                                    setIsOpen(false);
                                  }}
                                >
                                  <motion.div
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: index * 0.05 }}
                                    className={`flex items-center px-3 py-1.5 rounded-md cursor-pointer text-slate-600 hover:bg-slate-50 hover:text-[#3c0f55] text-xs ${
                                      location.pathname === sub.path ? 'bg-slate-50 text-[#3c0f55]' : ''
                                    }`}
                                  >
                                    {sub.name}
                                  </motion.div>
                                </Link>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            
            <div className="p-3">
              <div className="flex items-center gap-3 p-2">
                <img
                  src={user.avatar}
                  alt="Avatar"
                  className="h-8 w-8 rounded-full object-cover"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{user.name}</p>
                  <p className="text-xs text-gray-500 truncate">{user.email}</p>
                </div>
                <Link to="/profile">
                  <div className="p-1">
                    <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                  </div>
                </Link>
              </div>
            </div>
          </motion.aside>
        </AnimatePresence>
      )}

      <div
        className="flex-1 flex flex-col transition-all duration-300"
        style={{ marginLeft: !isMobile && isOpen ? '240px' : isMobile ? '0' : '70px' }}
      >
        {!isMobile && (
          <div className="h-14 flex-shrink-0 relative">
            <header
              className="fixed top-0 left-0 right-0 bg-white border-b border-slate-100 h-14 flex items-center justify-between px-6 z-40"
              style={{ paddingLeft: isOpen ? '250px' : '80px' }}
            >
              <div className="flex items-center space-x-3">
                <h1 className="text-lg font-semibold text-gray-800">{title}</h1>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={alternarTema}
                  className="cursor-pointer flex items-center justify-center h-8 w-8 rounded-full hover:bg-gray-100 transition-colors"
                >
                  {isDarkMode ? (
                    <SunIcon className="h-4 w-4 text-gray-600" />
                  ) : (
                    <MoonIcon className="h-4 w-4 text-gray-600" />
                  )}
                </button>

                <div className="relative" ref={notificacoesRef}>
                  <button
                    className="cursor-pointer flex items-center justify-center h-8 w-8 rounded-full hover:bg-gray-100 transition-colors"
                    onClick={alternarNotificacoes}
                  >
                    <SlBell size={14} className="text-gray-600" />
                  </button>

                  <AnimatePresence>
                    {notificacoesAberto && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-slate-200 py-2 z-50"
                      >
                        <div className="px-4 py-2 border-b border-slate-100">
                          <h3 className="text-sm font-medium text-gray-800">
                            Notificações
                          </h3>
                        </div>
                        <div className="max-h-64 overflow-y-auto">
                          <div className="px-4 py-3 text-sm text-gray-500 text-center">
                            Nenhuma notificação no momento
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                <div className="relative flex items-center justify-center">
                  <DropDown 
                    user={user} 
                    options={dropdownOptions}
                    trigger={
                      <img
                        src={user.avatar}
                        alt="Avatar"
                        className="h-6 w-6 rounded-full object-cover"
                      />
                    }
                  />
                </div>
              </div>
            </header>
          </div>
        )}

        {isMobile && (
          <div className="fixed top-0 left-0 right-0 z-40 px-4 py-4 flex flex-col">
            <div className="flex justify-between items-center">
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setIsOpen(true)}
              >
                <Bars3Icon className="h-6 w-6 text-gray-700" />
              </motion.button>
              
              <h1 className="text-lg font-semibold text-gray-800">{title}</h1>
            </div>
          </div>
        )}

        <main className={`flex-1 overflow-hidden relative ${isMobile ? 'pt-0' : ''}`}>
          <div className="absolute inset-0 overflow-y-auto">
            <div className={`${isMobile ? 'pt-24' : 'pt-6'} px-4 md:px-6 min-h-full`}>
              <Outlet />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default BaseLayout;